import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './index';

export interface ProcessMiningFunnelState {
  isLoading: boolean;
  isCompleted: boolean;
  error?: string;
  startedAt?: number; // timestamp for cleanup
  message: string | null;
}

export interface ProcessMiningState {
  funnelMaps: {
    [funnelMapId: string]: ProcessMiningFunnelState;
  };
}

const initialState: ProcessMiningState = {
  funnelMaps: {},
};

export const processMiningSlice = createSlice({
  name: 'processMining',
  initialState,
  reducers: {
    setProcessMiningLoading: (
      state,
      action: PayloadAction<{
        funnelMapId: string;
        isLoading: boolean;
      }>,
    ) => {
      const { funnelMapId, isLoading } = action.payload;

      state.funnelMaps[funnelMapId] = {
        isLoading,
        isCompleted: false,
        error: undefined,
        startedAt: isLoading ? Date.now() : undefined,
        message: null,
      };
    },
    setProcessMiningMessage: (
      state,
      action: PayloadAction<{
        funnelMapId: string;
        message: string | null;
      }>,
    ) => {
      const { funnelMapId, message } = action.payload;
      state.funnelMaps[funnelMapId] = {
        isLoading: false,
        message,
        isCompleted: false,
        error: undefined,
        startedAt: undefined,
      };
    },
    setProcessMiningCompleted: (
      state,
      action: PayloadAction<{
        funnelMapId: string;
      }>,
    ) => {
      const { funnelMapId } = action.payload;

      state.funnelMaps[funnelMapId] = {
        isLoading: false,
        isCompleted: true,
        error: undefined,
        startedAt: undefined,
        message: null,
      };
    },
    setProcessMiningError: (
      state,
      action: PayloadAction<{
        funnelMapId: string;
        error: string;
      }>,
    ) => {
      const { funnelMapId, error } = action.payload;

      state.funnelMaps[funnelMapId] = {
        isLoading: false,
        isCompleted: false,
        error,
        startedAt: undefined,
        message: null,
      };
    },
    clearProcessMiningState: (
      state,
      action: PayloadAction<{
        funnelMapId: string;
      }>,
    ) => {
      const { funnelMapId } = action.payload;

      delete state.funnelMaps[funnelMapId];
    },
    cleanupExpiredProcessMiningStates: (state) => {
      const now = Date.now();
      const TIMEOUT_DURATION = 10 * 60 * 1000;

      Object.keys(state.funnelMaps).forEach((funnelMapId) => {
        const funnelState = state.funnelMaps[funnelMapId];
        if (
          funnelState.isLoading &&
          funnelState.startedAt &&
          now - funnelState.startedAt > TIMEOUT_DURATION
        ) {
          // Convert expired loading states to error states
          state.funnelMaps[funnelMapId] = {
            isLoading: false,
            isCompleted: false,
            error: 'Process mining request timed out. Please try again.',
            startedAt: undefined,
            message: null,
          };
        }
      });
    },
  },
});

export const {
  setProcessMiningLoading,
  setProcessMiningCompleted,
  setProcessMiningError,
  clearProcessMiningState,
  cleanupExpiredProcessMiningStates,
  setProcessMiningMessage,
} = processMiningSlice.actions;

const defaultProcessMiningState: ProcessMiningFunnelState = {
  isLoading: false,
  isCompleted: false,
  error: undefined,
  startedAt: undefined,
  message: null,
};

export const selectProcessMiningState = (funnelMapId: string) => (state: RootState) =>
  state.processMining.funnelMaps[funnelMapId] || defaultProcessMiningState;

export const selectProcessMiningLoading = (funnelMapId: string) => (state: RootState) =>
  state.processMining.funnelMaps[funnelMapId]?.isLoading || false;

export const selectProcessMiningCompleted = (funnelMapId: string) => (state: RootState) =>
  state.processMining.funnelMaps[funnelMapId]?.isCompleted || false;

export const selectProcessMiningMessage = (funnelMapId: string) => (state: RootState) =>
  state.processMining.funnelMaps[funnelMapId]?.message || null;

export const selectProcessMiningError = (funnelMapId: string) => (state: RootState) =>
  state.processMining.funnelMaps[funnelMapId]?.error;

export const selectHasAnyProcessMiningInProgress = () => (state: RootState) => {
  return Object.values(state.processMining.funnelMaps).some(
    (funnelMapState) => funnelMapState.isLoading,
  );
};

export default processMiningSlice.reducer;
